import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgentDeviceView, DeviceAgentView, DeviceProfile, Platform } from '@1qa/shared';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';

const prefKey = (projectId: string) => `1qa.runDevice.${projectId}`;
const CHANGED = '1qa:run-device';

function readPref(projectId: string): string | null {
  try {
    return window.localStorage.getItem(prefKey(projectId)) || null;
  } catch {
    return null;
  }
}

function writePref(projectId: string, id: string | null) {
  try {
    if (id) window.localStorage.setItem(prefKey(projectId), id);
    else window.localStorage.removeItem(prefKey(projectId));
  } catch {
    /* storage unavailable: keep the choice for this page only */
  }
}

/** "Pixel 9 · Android 16 (1 ready)" for the picker. */
export const profileOptionLabel = (p: DeviceProfile) =>
  `${p.label}${p.ready ? ` (${p.ready} ready)` : p.total ? ' (starting)' : ''}`;

/** A phone on a Device Agent, for the picker: "Pixel 7 · Android 15 · Dana's laptop (busy)". */
export const phoneOptionLabel = (agent: DeviceAgentView, d: AgentDeviceView) =>
  `${d.label} · ${agent.name}${agent.paused ? ' (paused)' : d.status === 'ready' ? '' : d.status === 'busy' ? ' (busy)' : ' (not ready)'}`;

/**
 * The device profile an Android project's runs use (e.g. Pixel 9 · Android 16), remembered per project in this
 * browser and shared by every Run button. null means any device, also when the chosen profile is no longer offered.
 */
export function useRunDevice(projectId: string | undefined, platform: Platform | undefined) {
  const enabled = !!projectId && platform === 'ANDROID';
  // Same query (and cache) as the runner warning on the project page.
  const status = useQuery({ queryKey: ['runner-status'], queryFn: api.runnerStatus, refetchInterval: 30_000, staleTime: 15_000, enabled });
  // Phones on testers' computers (Device Agents), same query as the Devices page.
  const { currentOrgId } = useAuth();
  const agents = useQuery({
    queryKey: ['device-agents', currentOrgId],
    queryFn: () => api.deviceAgents(currentOrgId!),
    enabled: enabled && !!currentOrgId,
    refetchInterval: 15_000,
  });
  const [stored, setStored] = useState<string | null>(() => (projectId ? readPref(projectId) : null));

  useEffect(() => {
    if (!projectId) return;
    setStored(readPref(projectId));
    const sync = (e: Event) => {
      if ((e as CustomEvent<string>).detail === projectId) setStored(readPref(projectId));
    };
    window.addEventListener(CHANGED, sync);
    return () => window.removeEventListener(CHANGED, sync);
  }, [projectId]);

  const set = useCallback(
    (id: string | null) => {
      if (!projectId) return;
      writePref(projectId, id);
      setStored(id);
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: projectId }));
    },
    [projectId],
  );

  const profiles = enabled ? (status.data?.platforms.android.profiles ?? []) : [];
  const phones = enabled
    ? (agents.data ?? []).flatMap((a) => (a.online ? a.devices.filter((d) => d.platform === platform).map((d) => ({ agent: a, device: d })) : []))
    : [];
  const valid = stored && (profiles.some((p) => p.id === stored) || phones.some((p) => p.device.profile === stored)) ? stored : null;
  // Without emulators, "any device" has nowhere to run: use a connected phone (a ready one first).
  const fallback = profiles.length === 0 ? (phones.find((p) => p.device.status === 'ready') ?? phones[0])?.device.profile ?? null : null;
  const deviceProfile = valid ?? fallback;
  return { enabled, profiles, phones, deviceProfile, setDeviceProfile: set };
}

export type RunDeviceState = ReturnType<typeof useRunDevice>;
